import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { formatCurrencyExtended, formatDecimals } from '../../utils';
import { Button } from 'primereact/button';
import { useWeb3React } from "@web3-react/core";

const PlayPlatformModal = React.memo(({ 
    playPlatform, 
    token, 
    isVisible, 
    setShowModal, 
    getTokenLogoUrl, 
    poolType, 
    onQuickPlay, 
    txPending, 
    needsApproval,  // New prop for checking if approval is needed
    onApproveToken,  // New prop for handling token approval
    fetchPlayPlatform,
}) => {
    const { account } = useWeb3React();
    const { poolSettings, winnerInfo, total, lastRoll } = playPlatform || {};

    const isWinner = account && winnerInfo.previousWinner && account.toLowerCase() === winnerInfo.previousWinner.toLowerCase();

    useEffect(() => {
        if (isVisible) {
            fetchPlayPlatform(token);
        }
    }, [isVisible, fetchPlayPlatform, token]);

    return (
        <Dialog
            header={<div style={{ display: 'flex', alignItems: 'center' }}>
                {getTokenLogoUrl(token) && (
                    <img src={getTokenLogoUrl(token)} alt={token.label} style={{ width: '40px', height: '40px', marginRight: '10px' }} />
                )}
                <h2 style={{ margin: 0, color: '#333', fontSize: '24px' }}>{token.label} Play Platform</h2>
            </div>}
            visible={isVisible}
            style={{
                width: '95%', maxWidth: '450px', minWidth: '300px',
                maxHeight: '600px', overflow: 'auto',
                borderRadius: '25px',
                backgroundColor: '#fff',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
            }}
            onHide={() => setShowModal(false)}
        >
            {playPlatform && (
                <div style={{ padding: '20px' }}>
                    <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '15px', marginBottom: '15px' }}>
                        <h3 style={{ color: '#4a90e2', fontSize: '20px', marginBottom: '10px' }}>🎮 Pool Info</h3>
                        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            <strong>🎯 Winning Probability:</strong>
                            <span>{(poolSettings.chance / poolSettings.accuracy * 100).toFixed(2)}%</span>
                        </div>
                        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            <strong>💰 Required to Play:</strong>
                            <span>{formatDecimals(poolSettings.requiredAmount, token.decimals)} {token.label}</span>
                        </div>
                        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            <strong>⏳ Cool Down Time:</strong>
                            <span>{poolSettings.coolDownTime} seconds</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong>💼 Minimum Hold Amount:</strong>
                            <span>{formatDecimals(poolSettings.minimumHoldAmount, token.decimals)} {token.label}</span>
                        </div>
                    </div>

                    {needsApproval ? (
                        <Button
                            label="Approve"
                            onClick={() => onApproveToken(token)}
                            disabled={txPending} // Disable during transaction
                            severity="info"
                            style={{
                                width: '100%',
                                height: '50px',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                border: 'none',
                                borderRadius: '25px',
                                marginTop: '15px',
                                marginBottom: '15px',
                            }}
                        />
                    ) : (
                        <Button
                            label={txPending ? "Processing..."
                                : poolType === "Stake" ? "Quick StakePlay" : "Quick BurnPlay"}
                            onClick={() => onQuickPlay(token, poolSettings, poolType)}
                            disabled={txPending} // Disable during transaction
                            severity={poolType === "Stake" ? "warning" : "danger"}
                            style={{
                                width: '100%',
                                height: '50px',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                border: 'none',
                                borderRadius: '25px',
                                marginTop: '15px',
                                marginBottom: '15px',
                            }}
                        />
                    )}

                    <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '15px', marginBottom: '15px' }}>
                        <h3 style={{ color: '#4a90e2', fontSize: '20px', marginBottom: '10px' }}>🏆 Winner Info</h3>
                        {winnerInfo.previousWinner !== '0x0000000000000000000000000000000000000000' ? (
                            <>
                                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                                    <strong>🏅 Previous Win Amount:</strong>
                                    <span>{formatDecimals(winnerInfo.previousWonAmount, token.decimals)} {token.label}</span>
                                </div>
                                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                                    <strong>🕒 Previous Win Time:</strong>
                                    <span>{new Date(winnerInfo.previousWinTime * 1000).toLocaleString()}</span>
                                </div>
                                <div style={{
                                    marginBottom: '10px', color: isWinner ? '#d4af37' : '#2c3e50',
                                    fontWeight: isWinner ? 'bold' : 'normal', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                }}>
                                    <strong>🥇 Previous Winner:</strong>
                                    {isWinner ? (
                                        <span>
                                            🎉 YOU WON! 🎉 <br /> {winnerInfo.previousWinner} <br />
                                            <em style={{ color: '#d4af37' }}>Enjoy your rewards!</em>
                                        </span>
                                    ) : (
                                        <span>{winnerInfo.previousWinner}</span>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', fontStyle: 'italic', color: '#bdc3c7' }}>
                                No winner yet! Be the first to win! 🎉
                            </div>
                        )}
                    </div>

                    <div style={{ paddingBottom: '15px', marginBottom: '15px' }}>
                        <h3 style={{ color: '#4a90e2', fontSize: '20px', marginBottom: '10px' }}>📊 Platform Stats</h3>
                        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            <strong>📦 Total Rewards Pool:</strong>
                            <span>{formatDecimals(total, token.decimals)} {token.label}</span>
                        </div>
                        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            <strong>🎲 Last Roll Result:</strong>
                            <span>{lastRoll}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#f0f8ff', padding: '10px', borderRadius: '10px', }}>
                            <strong> 🎲 Winning Roll:</strong>
                            <span>Get a roll less than {poolSettings.chance} to win!</span>
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
});


export default PlayPlatformModal;
